import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import ReactPaginate from 'react-paginate';
import { Link } from 'react-router-dom';
import withPermission from '../../utils/withPermission';
import LicenseTypeModal from './LicenseTypeModal';

const LicenseTypeList = () => {
    const [licenseTypes, setLicenseTypes] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedTypeId, setSelectedTypeId] = useState(null);
    const [selectedTypeData, setSelectedTypeData] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageCount, setPageCount] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const debounceTimeoutRef = useRef(null);

    const fetchLicenseTypes = async (page = 1) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const token = userInfo?.token;
            const origin = localStorage.getItem('user-origin');

            const response = await axios.get(`${backendUrl}/api/license-types`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                     'Tenant-Origin': origin},
                
                params: {
                    page: page,
                    search: searchQuery,
                },
            });

            // If API returns paginated data:
            const dataObj = response.data.Data;
            if (dataObj && dataObj.data) {
                setLicenseTypes(dataObj.data);
                setCurrentPage(dataObj.current_page - 1);
                setPageCount(dataObj.last_page);
            } else {
                setLicenseTypes(dataObj || []);
                setPageCount(1);
            }
        } catch (error) {
            console.error('Error fetching license types:', error);
        }
    };

    useEffect(() => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
        debounceTimeoutRef.current = setTimeout(() => {
            fetchLicenseTypes(1);
        }, searchQuery ? 1500 : 0);

        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, [searchQuery]);

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    // Replace these with actual permissions if they exist
    const WithDeletePermission = withPermission(({ children }) => children, ['instance-delete']); // Update perm string as needed
    const WithEditPermission = withPermission(({ children }) => children, ['instance-edit']);
    const WithCreatePermission = withPermission(({ children }) => children, ['instance-create']);

    const addLicenseType = async (newType) => {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const userInfo = JSON.parse(localStorage.getItem('user-info'));
        const token = userInfo?.token;
        const origin = localStorage.getItem('user-origin');

        const response = await axios.post(`${backendUrl}/api/license-types`, newType, {
            headers: { 'Authorization': `Bearer ${token}`, 'Tenant-Origin': origin }
        });

        if (response.data && response.data.Data) {
            fetchLicenseTypes(currentPage + 1);
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'success',
                title: 'Added successfully!',
                timer: 3000,
                showConfirmButton: false,
            });
        }
    };

    const editLicenseType = async (id, updatedData) => {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const userInfo = JSON.parse(localStorage.getItem('user-info'));
        const token = userInfo?.token;
                    const origin = localStorage.getItem('user-origin');


        const response = await axios.put(`${backendUrl}/api/license-types/${id}`, updatedData, {
            headers: { 'Authorization': `Bearer ${token}` ,'Tenant-Origin': origin},
        });

        if (response.data && response.data.Data) {
            setLicenseTypes(prev => prev.map(t => t.id === id ? response.data.Data : t));
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'success',
                title: 'Updated successfully!',
                timer: 3000,
                showConfirmButton: false,
            });
        }
    };

    const deleteLicenseType = async (id) => {
        const origin = localStorage.getItem('user-origin');
        Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!"
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const backendUrl = process.env.REACT_APP_BACKEND_URL;
                    const userInfo = JSON.parse(localStorage.getItem('user-info'));
                    const token = userInfo?.token;
                    const origin = localStorage.getItem('user-origin');

                    await axios.delete(`${backendUrl}/api/license-types/${id}`, {
                        headers: { 'Authorization': `Bearer ${token}`, 'Tenant-Origin': origin }
                    });

                    setLicenseTypes(prev => prev.filter(t => t.id !== id));
                    Swal.fire("Deleted!", "The license type has been deleted.", "success");
                } catch (error) {
                    Swal.fire("Error!", "Failed to delete license type.", "error");
                }
            }
        });
    };

    const handleShowModal = (typeId = null) => {
        if (typeId) {
            const tToEdit = licenseTypes.find(t => t.id === typeId);
            if (tToEdit) {
                setSelectedTypeData(tToEdit);
            }
        } else {
            setSelectedTypeData(null);
        }
        setSelectedTypeId(typeId);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedTypeId(null);
        setSelectedTypeData(null);
    };

    const handlePageClick = (data) => {
        const selectedPage = data.selected + 1;
        fetchLicenseTypes(selectedPage);
    };

    return (
        <div className="main-content">
            <div className="page-content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                <h4 className="mb-sm-0 font-size-18">License Types</h4>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between mb-3">
                                        <div className="d-flex">
                                            <div className="search-box me-2 mb-2 d-inline-block">
                                                <div className="position-relative">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Search..."
                                                        value={searchQuery}
                                                        onChange={handleSearchChange}
                                                    />
                                                    <i className="bx bx-search-alt search-icon" />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            <WithCreatePermission>
                                                <button
                                                    type="button"
                                                    className="btn rounded-circle border d-flex align-items-center justify-content-center round-create-button"
                                                    onClick={() => handleShowModal()}
                                                    title='Add License Type'
                                                    style={{ width: '40px', height: '40px' }}
                                                >
                                                    <i className="mdi mdi-plus"></i>
                                                </button>
                                            </WithCreatePermission>
                                        </div>
                                    </div>

                                    <div>
                                        <table className="table align-middle table-nowrap table-check table-list">
                                            <thead className="table-sales-gradient">
                                                <tr>
                                                    <th style={{ width: '20px' }}>#</th>
                                                    <th>Name</th>
                                                    <th>Key</th>
                                                    <th>Type</th>
                                                    <th>Description</th>
                                                    <th>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {licenseTypes.length > 0 ? (
                                                    licenseTypes.map((type, index) => (
                                                        <tr key={type.id}>
                                                            <td>{(currentPage * 10) + index + 1}</td>
                                                            <td>{type.name}</td>
                                                            <td>{type.key}</td>
                                                            <td style={{ textTransform: 'capitalize' }}>{type.type || 'License'}</td>
                                                            <td>{type.description}</td>
                                                            <td>
                                                                <div className="dropdown">
                                                                    <i className="mdi mdi-dots-horizontal font-size-18"
                                                                        data-bs-toggle="dropdown" aria-expanded="false" style={{ cursor: 'pointer' }}></i>
                                                                    <ul className="dropdown-menu dropdown-menu-end font-size-14">
                                                                        <li>
                                                                            <WithEditPermission>
                                                                                <button className="dropdown-item" onClick={() => handleShowModal(type.id)}>
                                                                                    Edit
                                                                                </button>
                                                                            </WithEditPermission>
                                                                        </li>
                                                                        <li>
                                                                            <WithDeletePermission>
                                                                                <button className="dropdown-item" onClick={() => deleteLicenseType(type.id)}>
                                                                                    Delete
                                                                                </button>
                                                                            </WithDeletePermission>
                                                                        </li>
                                                                    </ul>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="4">No license types found</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>

                                        {pageCount > 1 && (
                                            <div className="d-flex justify-content-end pagination-sm">
                                                <ReactPaginate
                                                    previousLabel={'<'}
                                                    nextLabel={'>'}
                                                    breakLabel={'...'}
                                                    pageCount={pageCount}
                                                    marginPagesDisplayed={2}
                                                    pageRangeDisplayed={3}
                                                    onPageChange={handlePageClick}
                                                    containerClassName={'pagination pagination-rounded justify-content-end mb-2'}
                                                    pageClassName={'page-item'}
                                                    pageLinkClassName={'page-link'}
                                                    previousClassName={'page-item previous'}
                                                    previousLinkClassName={'page-link'}
                                                    nextClassName={'page-item next'}
                                                    nextLinkClassName={'page-link'}
                                                    breakClassName={'page-item'}
                                                    breakLinkClassName={'page-link'}
                                                    activeClassName={'active'}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <LicenseTypeModal
                        key={selectedTypeId || 'new'}
                        showModal={showModal}
                        handleClose={handleCloseModal}
                        selectedTypeId={selectedTypeId}
                        selectedTypeData={selectedTypeData}
                        addLicenseType={addLicenseType}
                        editLicenseType={editLicenseType}
                    />
                </div>
            </div>
        </div>
    );
};

export default LicenseTypeList;
